import type { Brain } from '../api/agents.js'
import { PRICING, PRICING_AS_OF, microPer1kFromUsdPerMTok } from './pricing.js'

/**
 * Brain catalog. Synced into `agents.brains` by `pnpm db:migrate` and `pnpm db:seed` (brains dropped from this list
 * are disabled there, never deleted: agents may reference them). `enabled: false` brains are listed but cannot be
 * chosen, and need no rate card.
 *
 * Every call goes through OpenRouter (docs/RUNTIME.md "Model gateway"): `anthropic/<model>` as
 * `PRICING[id].openrouterId`, `openrouter/<vendor>/<model>` as `<vendor>/<model>` (= `model`). `provider` is the model vendor (display); DeepSeek and
 * Z.ai are `other`.
 */
export const BRAINS = [
  { id: 'anthropic/claude-fable-5-1', provider: 'anthropic', model: 'claude-fable-5-1', displayName: 'Claude Fable 5.1', tier: 'frontier', enabled: true, sort: 10 },
  { id: 'anthropic/claude-opus-5-5', provider: 'anthropic', model: 'claude-opus-5-5', displayName: 'Claude Opus 5.5', tier: 'frontier', enabled: true, sort: 20 },
  { id: 'anthropic/claude-sonnet-5', provider: 'anthropic', model: 'claude-sonnet-5', displayName: 'Claude Sonnet 5', tier: 'frontier', enabled: true, sort: 30 },
  {
    id: 'anthropic/claude-haiku-4-5-20251001',
    provider: 'anthropic',
    model: 'claude-haiku-4-5-20251001',
    displayName: 'Claude Haiku 4.5',
    tier: 'cheap',
    enabled: true,
    sort: 40,
  },
  { id: 'openrouter/openai/gpt-5', provider: 'openai', model: 'openai/gpt-5', displayName: 'GPT-5', tier: 'frontier', enabled: true, sort: 50 },
  { id: 'openrouter/x-ai/grok-4.7', provider: 'xai', model: 'x-ai/grok-4.7', displayName: 'Grok 4.7', tier: 'frontier', enabled: true, sort: 60 },
  { id: 'openrouter/google/gemini-2.5-pro', provider: 'google', model: 'google/gemini-2.5-pro', displayName: 'Gemini 2.5 Pro', tier: 'frontier', enabled: true, sort: 70 },
  { id: 'openrouter/deepseek/deepseek-v4-pro', provider: 'other', model: 'deepseek/deepseek-v4-pro', displayName: 'DeepSeek V4 Pro', tier: 'frontier', enabled: true, sort: 80 },
  { id: 'openrouter/google/gemini-2.5-flash', provider: 'google', model: 'google/gemini-2.5-flash', displayName: 'Gemini 2.5 Flash', tier: 'cheap', enabled: true, sort: 90 },
  { id: 'openrouter/z-ai/glm-5.3-flash', provider: 'other', model: 'z-ai/glm-5.3-flash', displayName: 'GLM 5.3 Flash', tier: 'cheap', enabled: true, sort: 100 },
] as const satisfies readonly Brain[]

export type BrainCatalogId = (typeof BRAINS)[number]['id']

export function getBrain(id: string): Brain | undefined {
  return BRAINS.find((b) => b.id === id)
}

/** Default brain per tier for new agents. */
export const DEFAULT_BRAIN_ID: BrainCatalogId = 'anthropic/claude-sonnet-5'
/** Cheap default (interviews, rules-only agents' occasional calls): GLM 5.3 Flash through OpenRouter. */
export const DEFAULT_CHEAP_BRAIN_ID: BrainCatalogId = 'openrouter/z-ai/glm-5.3-flash'
/** Brain of the paid agent-creation interview (`creation.interview`: intent compile + persona): GLM 5.3 Flash through OpenRouter. */
export const DEFAULT_INTERVIEW_BRAIN: BrainCatalogId = 'openrouter/z-ai/glm-5.3-flash'

/**
 * Model slots (docs/CREATION-V2.md): a named job that needs a model gets its brain from here, never from a slug in
 * code. `brainId` is the configured default; when it is not in the catalog, disabled or has no rate card, the slot
 * falls back to the cheapest enabled brain of `fallbackTier` (`resolveModelSlot`).
 *
 * - `creation.basics`: GLM 5.3 Flash on OpenRouter (`z-ai/glm-5.3-flash`, verified against OpenRouter's public model
 *   list on 2026-09-27: 0.045 / 0.14 USD per MTok, tools and structured outputs supported).
 * - `creation.interview`: the same brain for the interview role's turns (paid from the thread budget).
 */
export const MODEL_SLOTS = {
  'creation.basics': { brainId: 'openrouter/z-ai/glm-5.3-flash', fallbackTier: 'cheap' },
  'creation.interview': { brainId: 'openrouter/z-ai/glm-5.3-flash', fallbackTier: 'cheap' },
} as const satisfies Record<string, { brainId: BrainCatalogId; fallbackTier: 'cheap' | 'frontier' }>

export type ModelSlot = keyof typeof MODEL_SLOTS

/** The brain of a model slot: its configured brain when usable, else the cheapest enabled brain of its fallback tier. */
export function resolveModelSlot(slot: ModelSlot, usable: (brainId: string) => boolean = (id) => getBrain(id)?.enabled === true && RATE_CARDS[id] !== undefined): string {
  const s = MODEL_SLOTS[slot]
  if (usable(s.brainId)) return s.brainId
  const cost = (id: string) => {
    const r = RATE_CARDS[id]
    return r ? Number(r.inputMicroPer1k + r.outputMicroPer1k) : Number.POSITIVE_INFINITY
  }
  const byCost = BRAINS.filter((b) => b.tier === s.fallbackTier && usable(b.id)).sort((a, b) => cost(a.id) - cost(b.id) || a.sort - b.sort)
  if (byCost[0]) return byCost[0].id
  return s.brainId
}

/** Id prefix of brains served only through OpenRouter. */
export const OPENROUTER_BRAIN_PREFIX = 'openrouter/'

/**
 * OpenRouter model id of a brain: `openrouter/<vendor>/<model>` → `<vendor>/<model>`, a catalog brain → its
 * pricing entry's `openrouterId` (`anthropic/claude-sonnet-5` → `anthropic/claude-sonnet-5`,
 * `anthropic/claude-opus-5-5` → `anthropic/claude-opus-5.5`); undefined when OpenRouter cannot serve it.
 */
export function openRouterModelId(brainId: string): string | undefined {
  if (brainId.startsWith(OPENROUTER_BRAIN_PREFIX)) return brainId.slice(OPENROUTER_BRAIN_PREFIX.length)
  return PRICING[brainId]?.openrouterId
}

export interface CatalogRateCard {
  inputMicroPer1k: bigint
  outputMicroPer1k: bigint
  /** Explicit cache prices (migration 0029); null = multipliers of the input price (0.1x read, 1.25x / 2x write). */
  cacheReadMicroPer1k: bigint | null
  cacheWriteMicroPer1k: bigint | null
  cacheWrite1hMicroPer1k: bigint | null
  markupBps: number
}

/** Platform markup on provider list prices: 30 %. */
export const RATE_CARD_MARKUP_BPS = 3000

/** Date of the newest list prices (`PRICING_AS_OF`); each brain's card uses its own `PRICING[id].asOf`. */
export const RATE_CARDS_AS_OF = PRICING_AS_OF

/** List prices, USD per million tokens (input, output), for every enabled brain. */
export const LIST_PRICES_USD_PER_MTOK: Record<string, { input: number; output: number }> = Object.fromEntries(
  BRAINS.filter((b) => b.enabled).map((b) => {
    const p = PRICING[b.id]
    if (!p) throw new Error(`no list price for enabled brain ${b.id}`)
    return [b.id, { input: p.input, output: p.output }]
  }),
)

/**
 * Rate cards (provider cost in micro-credits per 1k tokens, plus markup) for every enabled brain, from `PRICING`.
 * Synced by `pnpm db:migrate` (replacing placeholder and older list cards) and the seed. The charged amount is
 * `ceil(cost × (10 000 + markupBps) / 10 000)`, see docs/ECONOMICS.md.
 */
export const RATE_CARDS: Record<string, CatalogRateCard> = Object.fromEntries(
  BRAINS.filter((b) => b.enabled).map((b) => {
    const p = PRICING[b.id]!
    return [
      b.id,
      {
        inputMicroPer1k: microPer1kFromUsdPerMTok(p.input),
        outputMicroPer1k: microPer1kFromUsdPerMTok(p.output),
        cacheReadMicroPer1k: microPer1kFromUsdPerMTok(p.cacheRead),
        cacheWriteMicroPer1k: microPer1kFromUsdPerMTok(p.cacheWrite),
        cacheWrite1hMicroPer1k: p.cacheWrite1h !== undefined ? microPer1kFromUsdPerMTok(p.cacheWrite1h) : null,
        markupBps: RATE_CARD_MARKUP_BPS,
      },
    ]
  }),
)

/** @deprecated the placeholders were replaced by `RATE_CARDS` (real list prices); kept as an alias. */
export const PLACEHOLDER_RATE_CARDS: Record<string, CatalogRateCard> = RATE_CARDS
